import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import Pagination from "../../../components/Common/Pagination";
import AddMember from "../people/AddMember";

const Member = ({ isDeveloper }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [open, setOpenModal] = useState(false);
  const [memberEditData, setMemberEditData] = useState(null);
  const [memberSearchData, setMemberSearchData] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [openMenuUserId, setOpenMenuUserId] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const peopleData = useSelector((state) => state.people.people.data);
  const peopleTotal = useSelector((state) => state.people.peopleTotal);
  const peopleLimit = useSelector((state) => state.people.peopleLimit);
  const isSuperAdmin = useSelector((state) =>
    state.auth.userInfoData?.roles?.some((role) => role.name === "Super Admin")
  );

  const pageLimit = 12;
  const pageCount = Math.ceil(peopleTotal / pageLimit);

  const getPeopleListData = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch.people.listPeopleData({
        limit: pageLimit,
        currentPage,
        search: memberSearchData,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, currentPage, memberSearchData]);

  useEffect(() => {
    getPeopleListData();
  }, [getPeopleListData]);

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    const userId = userToDelete._id || userToDelete.id || userToDelete.user_id;
    setIsDeleting(true);
    try {
      const res = await dispatch.people.deleteUser(userId);
      toast.success(res?.message || "User deleted successfully");
      setUserToDelete(null);
      getPeopleListData();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(
        error?.message ||
          error?.response?.data?.message ||
          "Failed to delete user"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePageClick = async (event) => {
    const selected = event.selected;
    setCurrentPage(selected);
    setLoading(true);
    await dispatch.people.listPeopleData({
      limit: peopleLimit,
      currentPage: selected,
      search: memberSearchData,
    });
    setLoading(false);
  };

  const handleModal = () => setOpenModal((prev) => !prev);

  useEffect(() => {
    if (!open) setMemberEditData(null);
  }, [open]);

  const onSuccess = () => getPeopleListData();

  return (
    <>
      <div className="container mx-auto ">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-center justify-between mt-8 lg:mt-[44px] mb-8">
          <div className="flex items-center justify-between w-full md:w-auto mb-5 md:mb-0">
            <h3 className="text-3xl font-light">All Members</h3>
          </div>
          <div className="flex sm:flex-row justify-between items-center w-full md:w-auto">
            {!isDeveloper && (
              <button
                onClick={handleModal}
                className="flex items-center bg-white text-black text-[10px] sm:text-xs transition duration-300 text-nowrap ease-out hover:bg-opacity-80 h-8 px-5 rounded-full border text-opacity-70 mr-4"
              >
                <span className="text-base sm:text-xl flex mr-2 -mt-[3px]">
                  {/* Add Member SVG */}
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 11 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.20702 0C6.95822 0 8.37777 1.4113 8.37777 3.1526C8.37818 3.60891 8.27897 4.0598 8.08708 4.47379C7.89518 4.88779 7.61523 5.2549 7.26677 5.5495C7.41527 5.62283 7.55057 5.69672 7.67267 5.77115C7.9352 5.93065 8.20837 6.12847 8.49217 6.3646C8.53045 6.39637 8.56206 6.4354 8.58517 6.47944C8.60829 6.52349 8.62246 6.57167 8.62685 6.62122C8.63125 6.67077 8.62579 6.7207 8.61079 6.76812C8.5958 6.81555 8.57155 6.85954 8.53947 6.89755C8.47447 6.97469 8.38158 7.02297 8.2811 7.03183C8.18062 7.0407 8.08071 7.00942 8.00322 6.94485C7.77427 6.75107 7.53124 6.57458 7.27612 6.41685C7.02808 6.26971 6.76984 6.1405 6.50337 6.0302C6.09526 6.2116 5.65362 6.30529 5.20702 6.3052C4.71468 6.30527 4.229 6.19138 3.78802 5.97245L3.76492 5.98345C2.69242 6.39925 1.90977 7.01415 1.40432 7.83035C0.897217 8.6493 0.689867 9.5645 0.782817 10.588C0.791353 10.6882 0.759772 10.7877 0.695011 10.8647C0.630249 10.9416 0.537602 10.9897 0.437417 10.9984C0.337295 11.0075 0.237624 10.9766 0.160284 10.9124C0.0829452 10.8481 0.0342597 10.7558 0.0249168 10.6557C-0.0823332 9.4721 0.161867 8.39355 0.756417 7.4338C1.27892 6.589 2.04727 5.9323 3.05212 5.4637C2.73195 5.16988 2.47636 4.81271 2.30158 4.41485C2.1268 4.017 2.03663 3.58715 2.03682 3.1526C2.03682 1.4113 3.45582 0 5.20702 0ZM9.27757 6.98115C9.48657 6.98115 9.65597 7.15165 9.65597 7.3612V8.3248H10.6196C10.6695 8.32466 10.7189 8.33435 10.765 8.35332C10.8111 8.37229 10.8531 8.40017 10.8884 8.43537C10.9238 8.47056 10.9518 8.51239 10.971 8.55844C10.9902 8.60449 11.0001 8.65387 11.0002 8.70375C10.9997 8.8043 10.9594 8.90057 10.888 8.97142C10.8167 9.04226 10.7201 9.08189 10.6196 9.0816H9.65597V10.0457C9.65611 10.1463 9.61635 10.2428 9.5454 10.3141C9.47445 10.3853 9.37812 10.4255 9.27757 10.4258C9.17701 10.4255 9.08068 10.3853 9.00974 10.3141C8.93879 10.2428 8.89902 10.1463 8.89917 10.0457V9.0816H7.93557C7.83501 9.08189 7.73845 9.04226 7.66709 8.97142C7.59573 8.90057 7.5554 8.8043 7.55497 8.70375C7.55497 8.49475 7.72547 8.32535 7.93557 8.32535L8.89917 8.3248V7.3612C8.89902 7.26065 8.93879 7.16414 9.00974 7.09289C9.08068 7.02163 9.17701 6.98144 9.27757 6.98115ZM5.20757 0.7568C3.87657 0.7568 2.79747 1.8293 2.79747 3.1526C2.79747 4.4759 3.87657 5.54895 5.20702 5.54895C6.53747 5.54895 7.61657 4.47645 7.61657 3.1526C7.61657 1.82875 6.53802 0.7568 5.20757 0.7568Z"
                      fill="#545454"
                    />
                  </svg>
                </span>
                Add Member
              </button>
            )}
            <div className="relative flex w-full md:w-auto">
              <input
                type="text"
                placeholder="Search..."
                className="appearance-none focus:outline-none focus:border-primary-navyBlue border text-xs px-6 rounded-full w-full placeholder-secondary-grey-300 h-9 md:min-w-80"
                onChange={(evt) => {
                  setCurrentPage(0);
                  setMemberSearchData(evt.target.value);
                }}
              />
              <svg
                width="16"
                height="16"
                className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.75 15.9494L11.4928 12.4441..."
                  stroke="#B0B0B0"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Member List */}
        <div className="">
          <div className="bg-white rounded flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 items-center justify-center">
              <AnimatePresence>
                {!loading && peopleData?.length > 0 ? (
                  peopleData.map((item) => {
                    const roles = item.roles?.map((r) => r.name).join(", ");
                    return (
                      <motion.div
                        key={item.id || item.email}
                        layout
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                        className="relative border px-5 pb-2 pt-10 rounded-md flex flex-col justify-between h-full"
                      >
                        <div>
                          {/* Role Badge */}
                          {(item?.roles?.find((r) => !r.type || r.type === "Main")?.name || item?.roles?.[0]?.name) && (
                            <div className="absolute right-0 bg-[#D2D2D2] px-3 py-1 text-[10px] text-white rounded-bl-xl rounded-tr-md top-0">
                              {item.roles.find((r) => !r.type || r.type === "Main")?.name || item.roles[0].name}
                            </div>
                          )}

                          {/* Header */}
                          <div className="flex items-center gap-4 border-b pb-4">
                            <img
                              src="/images/user-profile-avatar.svg"
                              alt="User Avatar"
                              className="bg-gray-200 rounded-full w-12"
                            />
                            <div>
                              <h4 className="text-base xl:text-lg 2xl:text-xl font-medium">
                                {item.first_name || "-"} {item.last_name || ""}
                              </h4>
                              {item.designation?.name && (
                                <p className="text-[#545454] text-sm font-normal mt-1 xl:mt-[2px] 2xl:mt-1 opacity-80">
                                  {item.designation.name}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Info */}
                          <div className="pt-6">
                            {["Email", "Current Project"].map((label, idx) => (
                              <div
                                key={idx}
                                className="flex flex-col items-start gap-1 py-2"
                              >
                                <p className="text-[10px] font-light opacity-80">
                                  {label}
                                </p>
                                <h6 className="text-sm font-medium">
                                  {label === "Email"
                                    ? item.email
                                    : item.projectList?.join(", ")}
                                </h6>
                              </div>
                            ))}

                            {/* Skills */}
                            {item.skills?.length > 0 && (
                              <div className="flex flex-col gap-1">
                                <p className="text-[10px] font-light opacity-80 py-2">
                                  Skills
                                </p>
                                <div className="flex flex-wrap gap-2 pb-6">
                                  {item.skills.map((skill) => (
                                    <button
                                      key={skill.name}
                                      className="px-4 rounded-sm py-2 bg-[#F2FAF4] text-[#057D54] text-xs font-normal"
                                    >
                                      {skill.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Manage Button */}
                        {!isDeveloper && (
                          <div className="flex items-center justify-between pt-4 border-t relative">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  handleModal();
                                  setMemberEditData(item);
                                }}
                                className="flex items-center justify-center px-4 py-2 gap-2 rounded-md shadow-sm border text-xs"
                              >
                                <svg
                                  width="17"
                                  height="17"
                                  viewBox="0 0 17 17"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fill-rule="evenodd"
                                    clip-rule="evenodd"
                                    d="M15.1973 3.83635C15.2646 3.9385 15.2946 4.06074 15.2822 4.18244C15.2698 4.30413 15.2158 4.41782 15.1293 4.50431L8.61763 11.0153C8.55101 11.0819 8.4679 11.1296 8.3768 11.1534L5.66459 11.8618C5.57493 11.8852 5.48072 11.8847 5.39131 11.8604C5.30189 11.8361 5.22038 11.7889 5.15487 11.7234C5.08935 11.6579 5.04211 11.5763 5.01783 11.4869C4.99355 11.3975 4.99308 11.3033 5.01646 11.2136L5.7248 8.50214C5.74598 8.42094 5.78493 8.34546 5.83884 8.28114L12.3746 1.7496C12.4742 1.65012 12.6093 1.59424 12.75 1.59424C12.8908 1.59424 13.0259 1.65012 13.1255 1.7496L15.1293 3.75277C15.1541 3.77886 15.1768 3.80681 15.1973 3.83635ZM14.0024 4.12819L12.75 2.87656L6.71646 8.91014L6.27376 10.6052L7.9688 10.1625L14.0024 4.12819Z"
                                    fill="#545454"
                                  />
                                  <path
                                    d="M13.9124 12.1551C14.106 10.5004 14.1679 8.83288 14.0973 7.16839C14.0956 7.12916 14.1021 7.09001 14.1164 7.05343C14.1307 7.01685 14.1524 6.98363 14.1802 6.95589L14.8772 6.25889C14.8962 6.23973 14.9204 6.22649 14.9468 6.22074C14.9732 6.21499 15.0007 6.21699 15.026 6.22649C15.0512 6.23599 15.0732 6.25259 15.0893 6.2743C15.1054 6.296 15.1149 6.32189 15.1166 6.34885C15.2474 8.32598 15.1976 10.3109 14.9678 12.279C14.8007 13.7113 13.6503 14.834 12.2245 14.9933C9.74908 15.2673 7.25102 15.2673 4.77563 14.9933C3.35047 14.834 2.19943 13.7113 2.03226 12.279C1.73908 9.76824 1.73908 7.23187 2.03226 4.7211C2.19943 3.28885 3.34976 2.16614 4.77563 2.00676C6.65445 1.79925 8.5473 1.74879 10.4345 1.85589C10.4615 1.85783 10.4874 1.86747 10.5091 1.88366C10.5308 1.89986 10.5474 1.92194 10.5569 1.94728C10.5665 1.97262 10.5686 2.00016 10.5629 2.02665C10.5573 2.05313 10.5442 2.07745 10.5252 2.09672L9.8218 2.79939C9.7943 2.8269 9.76143 2.84846 9.72524 2.86272C9.68905 2.87698 9.6503 2.88363 9.61143 2.88226C8.03663 2.82832 6.45999 2.88869 4.89393 3.06289C4.4363 3.11354 4.00911 3.31698 3.68137 3.64036C3.35362 3.96373 3.14446 4.38815 3.08768 4.84505C2.80353 7.27344 2.80353 9.72667 3.08768 12.1551C3.14446 12.612 3.35362 13.0364 3.68137 13.3598C4.00911 13.6831 4.4363 13.8866 4.89393 13.9372C7.27038 14.2028 9.72972 14.2028 12.1069 13.9372C12.5645 13.8866 12.9917 13.6831 13.3194 13.3598C13.6472 13.0364 13.8556 12.612 13.9124 12.1551Z"
                                    fill="#545454"
                                  />
                                </svg>
                                Manage
                              </button>
                              
                              {isSuperAdmin && (
                                <button
                                  onClick={() => {
                                    navigate("/messages", { state: { selectedUser: item } });
                                  }}
                                  className="flex items-center justify-center px-4 py-2 gap-2 rounded-md shadow-sm border text-xs"
                                >
                                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 7V17C4 18.1046 4.89543 19 6 19H18C19.1046 19 20 18.1046 20 17V7M4 7C4 5.89543 4.89543 5 6 5H18C19.1046 5 20 5.89543 20 7M4 7L12 12L20 7" stroke="#545454" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Message
                                </button>
                              )}
                            </div>

                            {/* 3 Vertical Dots Menu (Bottom Right) */}
                            <div className="relative">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const uId = item._id || item.id || item.user_id;
                                  setOpenMenuUserId((prev) => (prev === uId ? null : uId));
                                }}
                                className="w-8 h-8 rounded-full bg-transparent hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors focus:outline-none"
                                title="User Options"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="5" r="1" fill="currentColor" />
                                  <circle cx="12" cy="12" r="1" fill="currentColor" />
                                  <circle cx="12" cy="19" r="1" fill="currentColor" />
                                </svg>
                              </button>

                              {/* Dropdown Menu (opens upwards) */}
                              {openMenuUserId === (item._id || item.id || item.user_id) && (
                                <>
                                  <div
                                    className="fixed inset-0 z-20"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuUserId(null);
                                    }}
                                  />
                                  <div className="absolute right-0 bottom-full mb-1 w-36 bg-white rounded-lg shadow-lg border border-gray-200 z-30 py-1 overflow-hidden text-xs">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenMenuUserId(null);
                                        setUserToDelete(item);
                                      }}
                                      className="w-full text-left px-3 py-2 text-red-600 hover:bg-red-50 font-medium flex items-center gap-2 transition-colors"
                                    >
                                      <svg
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      >
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                      </svg>
                                      <span>Delete User</span>
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })
                ) : (
                  <p className="text-sm text-title col-span-full text-center py-10">
                    {loading ? "Loading..." : "No data"}
                  </p>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Pagination */}
          {!loading && peopleTotal > pageLimit && (
            <Pagination
              handlePageClick={handlePageClick}
              pageCount={pageCount}
              pageLimit={pageLimit}
              currentPage={currentPage}
            />
          )}
        </div>
      </div>

      {/* Add Member Modal */}
      <AddMember
        onSuccess={onSuccess}
        memberEditData={memberEditData}
        closeModal={handleModal}
        SetOpenModal={setOpenModal}
        open={open}
      />

      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !isDeleting && setUserToDelete(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 text-center">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dc2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  <line x1="10" y1="11" x2="10" y2="17" />
                  <line x1="14" y1="11" x2="14" y2="17" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">
                Delete User
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-slate-700">
                  {userToDelete.first_name || ""} {userToDelete.last_name || ""}
                </span>
                ? This action cannot be undone.
              </p>
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setUserToDelete(null)}
                  disabled={isDeleting}
                  className="px-5 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className={`px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg flex items-center gap-2 transition-colors ${
                    isDeleting ? "opacity-70 cursor-not-allowed" : "hover:bg-red-700"
                  }`}
                >
                  {isDeleting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{" "}
                      Deleting...
                    </>
                  ) : (
                    "Yes, Delete"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Member;
